import React, {useEffect, useState} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import './App.scss';
import './App.less';
import Tracker from './pages/Tracker';
import {slide as Menu} from 'react-burger-menu';
import ceiasLogo from './assets/images/ceias-logo.svg';
import About from './pages/About';
import Analyses from './pages/Analyses';
import Team from './pages/Team';
import moment from 'moment';
import CookieConsent from 'react-cookie-consent';
import {
  DownloadOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  TwitterOutlined,
} from '@ant-design/icons';

interface CountryCode {
  COUNTRY: string;
  CODE: string;
  TYPE: 'EU' | 'SEA';
}

interface IssueCode {
  ISSUE: string;
  CODE: string;
}

interface Team {
  NAME: string;
  TITLE: string;
  EMAIL: string;
  PICTURE: string;
  CEIAS_LINK: string;
}

interface Analysis {
  TITLE: string;
  DATE: moment.Moment;
  AUTHOR: string;
  LINK: string;
  PICTURE: string;
}

export interface AppState {
  countryCodes: CountryCode[];
  issueCodes: IssueCode[];
  trackerData: DataRow[];
  team: Team[];
  analyses: Analysis[];
}

export interface DataRow {
  id: string;
  key: string;
  date: moment.Moment;
  euCountries: string[];
  seaCountries: string[];
  topics: string[];
  event: string;
  links: string[];
  tw_link: string;
}

function escapeCsvField(str: string) {
  // If the field contains a comma, quote, newline, etc.,
  // wrap it in quotes and escape internal quotes
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function App() {
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [issueCodes, setIssueCodes] = useState<IssueCode[]>([]);
  const [trackerData, setTrackerData] = useState<any[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [analyses, setAnalyses] = useState<any[]>([]);

  // Load all data
  useEffect(() => {
    fetch(
        'https://opensheet.elk.sh/1u5OtQgLGGsdCLykWnQhP4jkbInH5mpeq6C9skArqj80/Country%20Codes',
    )
        .then((res) => res.json())
        .then((data: CountryCode[]) => {
          setCountryCodes(data);
        });
    fetch(
        'https://opensheet.elk.sh/1u5OtQgLGGsdCLykWnQhP4jkbInH5mpeq6C9skArqj80/Issue%20Codes',
    )
        .then((res) => res.json())
        .then((data: IssueCode[]) => {
          setIssueCodes(data);
        });
    fetch(
        'https://opensheet.elk.sh/1u5OtQgLGGsdCLykWnQhP4jkbInH5mpeq6C9skArqj80/Tracker%20(merged)',
    )
        .then((res) => res.json())
        .then((data: IssueCode[]) => {
          const processedData: DataRow[] = data.map((d: any, i: number) => {
            return {
              id: d.ID,
              key: `${d.ID}-${i}`,
              date: moment(d.DATE),
              euCountries: (d['EU COUNTRY CODE'] || '')
                  .split(',')
                  .map((c: string) => c && c.trim())
                  .filter((c: string) => c),
              seaCountries: (d['SEA COUNTRY CODE'] || '')
                  .split(',')
                  .map((c: string) => c && c.trim())
                  .filter((c: string) => c),
              topics: (d['ISSUE CODE'] || '')
                  .split(',')
                  .map((c: string) => c && c.trim())
                  .filter((c: string) => c),
              event: d.EVENT,
              links: [
                d['HYPERLINK 1'],
                d['HYPERLINK 2'],
                d['HYPERLINK 3'],
                d['HYPERLINK 4'],
                d['HYPERLINK 5'],
                d['HYPERLINK 6'],
                d['HYPERLINK 7'],
              ].filter((l) => l && l.trim()),
              tw_link: d['TW HYPERLINK'],
            };
          });
          processedData.sort((a, b) => {
            return b.date.diff(a.date);
          });
          setTrackerData(processedData);
        });
    fetch(
        'https://opensheet.elk.sh/1u5OtQgLGGsdCLykWnQhP4jkbInH5mpeq6C9skArqj80/Team',
    )
        .then((res) => res.json())
        .then((data) => {
          setTeam(data);
        });
    fetch(
        'https://opensheet.elk.sh/1u5OtQgLGGsdCLykWnQhP4jkbInH5mpeq6C9skArqj80/Analyses',
    )
        .then((res) => res.json())
        .then((data: Analysis[]) => {
          const processedData: Analysis[] = data.map((d: any) => {
            return {
              ...d,
              DATE: moment(d.DATE),
            };
          });
          processedData.sort((a, b) => {
            return b.DATE.diff(a.DATE);
          });
          setAnalyses(processedData);
        });
  }, []);

  const appState = {
    countryCodes,
    issueCodes,
    trackerData,
    team,
    analyses,
  };

  const downloadCsv = () => {
    const data = trackerData.map((d) => {
      return {
        Date: d.date.format('YYYY-MM-DD'),
        EUCountries: d.euCountries.join(', '),
        SEACountries: d.seaCountries.join(', '),
        Topics: d.topics.join(', '),
        Event: d.event,
        Links: d.links.join(', '),
        TaiwanSourceLink: d.tw_link,
      };
    });

    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map((row) => Object.values(row).map(escapeCsvField).join(',')),
    ].join('\n');

    const blob = new Blob([csv], {type: 'text/csv'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'ceias-tracker.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="App">
      <div id="outer-container">
        <Menu pageWrapId={'page-wrap'} noOverlay width={240} right>
          <a id="home" className="menu-item" href="/">
            Tracker
          </a>
          <a id="analyses" className="menu-item" href="/analyses">
            Analyses
          </a>
          <a id="about" className="menu-item" href="/about">
            About
          </a>
          <a id="team" className="menu-item" href="/team">
            Team
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              downloadCsv();
            }}
          >
            <DownloadOutlined />
            &nbsp;Download Data
          </a>
        </Menu>
        <a href="/" className="page-header">
          <div className="logo">
            <img src={ceiasLogo} alt="CEIAS" />
          </div>
        </a>
        <main id="page-wrap">
          <Routes>
            <Route path="/" element={<Navigate to="/tracker" />} />
            <Route path="/tracker" element={<Tracker state={appState} />} />
            <Route path="/tracker/:id" element={<Tracker state={appState} />} />
            <Route path="/analyses" element={<Analyses state={appState} />} />
            <Route path="/about" element={<About state={appState} />} />
            <Route path="/team" element={<Team state={appState} />} />
          </Routes>
        </main>
      </div>
      <div className="footer">
        <div>
          <p className="copyright">
            © CEIAS {new Date().getFullYear()}. All rights reserved.
          </p>
          <p>
            <a href="https://ceias.eu">www.ceias.eu</a>
            <span>&nbsp;|&nbsp;</span>
            <a href="mailto:office@ceias.eu">office@ceias.eu</a>
          </p>
        </div>
        <div className="funding-info">
          <p>
            <strong>Donor:</strong> Funded by the EU NextGenerationEU through
            the Recovery and Resilience Plan for Slovakia under project No.
            09I03-03-V04-00462.
          </p>
          <p>
            <strong>Duration:</strong> August 2024-July 2026
          </p>
          <img src="/plan-obnovy.png" alt="Plan Obnovy" />
        </div>
        <div className="social">
          <a href="https://www.facebook.com/CEIASeu">
            <FacebookOutlined style={{fontSize: 28}} />
          </a>
          <a href="https://twitter.com/ceias_eu">
            <TwitterOutlined style={{fontSize: 28}} />
          </a>
          <a href="https://www.linkedin.com/company/ceias-eu">
            <LinkedinOutlined style={{fontSize: 28}} />
          </a>
          <a href="https://www.instagram.com/ceias_eu/">
            <InstagramOutlined style={{fontSize: 28}} />
          </a>
        </div>
      </div>
      <CookieConsent>
        This website uses cookies to enhance the user experience.
      </CookieConsent>
    </div>
  );
}

export default App;
